import { Box, Tooltip, Typography } from "@mui/material";
import { RefreshCw } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { t } from "i18next";
import { getReviewStates } from "../../../services/inspectService";
import { FilterState } from "../../../@types/services/inspStatusService";
import { SF } from "../../../utils/utils";

const CELL = 9;
const GAP = 2;
const ROW_H = CELL + GAP;
const VIEW_H = 120; // 잔디 스크롤 영역 높이
const CHUNK = 1000; // 서버에서 받아오는 구간 크기 (수백만 건이라 전체는 못 받는다)
const WINDOW = CHUNK * 2;
const POLL_MS = 5000;
const IDLE_LIMIT = 3; // 변화 없는 조회가 이만큼 연속되면 폴링 중단

const COLORS: Record<string, string> = {
  "0": "#d2d2d7", // 미리뷰
  "1": "#30a14e", // OK
  "2": "#dc2626", // NG
  "3": "#2563eb", // 그 외 라벨
};
const EMPTY = "#f2f2f4";

interface Props {
  filters: FilterState;
  selectedId?: number;
  reviewTick: number;
  onJump: (id: number, ascIndex: number, total: number) => void;
}

export default function ReviewGrass({ filters, selectedId, reviewTick, onJump }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(20);
  const [scrollTop, setScrollTop] = useState(0);
  const [total, setTotal] = useState(0);
  const [win, setWin] = useState<{
    offset: number;
    ids: number[];
    states: string;
  }>({
    offset: 0,
    ids: [],
    states: "",
  });
  const [paused, setPaused] = useState(false);

  // 폴링 재개 트리거. 멈춘 뒤 새로고침을 누르면 증가한다
  const [resume, setResume] = useState(0);
  const idleRef = useRef(0);
  const sigRef = useRef("");
  const didScrollToEnd = useRef(false);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setCols(Math.max(1, Math.floor(el.clientWidth / ROW_H))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rowCount = Math.ceil(total / cols);
  const firstRow = Math.max(0, Math.floor(scrollTop / ROW_H) - 1);
  const lastRow = Math.min(rowCount, Math.ceil((scrollTop + VIEW_H) / ROW_H) + 1);

  // 보이는 구간을 덮는 CHUNK 경계. 스크롤마다 요청이 나가지 않도록 경계로 끊는다
  const chunkStart = Math.max(0, Math.floor((firstRow * cols) / CHUNK) * CHUNK);
  const filterKey = JSON.stringify(filters);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    // 조회 대상이 바뀌었으니 "변화 없음" 카운트도 처음부터
    idleRef.current = 0;
    sigRef.current = "";
    setPaused(false);

    const tick = async () => {
      // 이 화면이 보일 때만 조회 (팝업으로 띄운 경우 팝업 문서 기준)
      const doc = scrollRef.current?.ownerDocument ?? document;
      if (!doc.hidden) {
        const res = await getReviewStates({
          ...filters,
          offset: chunkStart,
          limit: WINDOW,
        });
        const d = res?.data;
        if (cancelled) return;
        if (d) {
          const sig = `${d.total}:${d.states}`;
          idleRef.current = sig === sigRef.current ? idleRef.current + 1 : 0;
          sigRef.current = sig;
          setTotal(d.total);
          setWin({ offset: d.offset, ids: d.ids, states: d.states });
          if (idleRef.current >= IDLE_LIMIT) {
            setPaused(true);
            return;
          }
        }
      }
      timer = setTimeout(tick, POLL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, chunkStart, reviewTick, resume]);

  // 최초 로드 시 가장 최근 데이터(맨 끝)로 내려둔다
  useEffect(() => {
    if (didScrollToEnd.current || !rowCount || !scrollRef.current) return;
    didScrollToEnd.current = true;
    scrollRef.current.scrollTop = rowCount * ROW_H;
  }, [rowCount]);

  const selectedIdx = selectedId ? win.ids.indexOf(selectedId) : -1;

  const legend: [string, string][] = [
    ["1", "OK"],
    ["2", "NG"],
    ["3", t("OtherLabel", "기타")],
    ["0", t("NotReviewed", "미리뷰")],
  ];

  const rows = [];
  for (let r = firstRow; r < lastRow; r++) {
    const cells = [];
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      if (i >= total) break;
      const local = i - win.offset;
      const s = win.states[local];
      const id = win.ids[local];
      cells.push(
        <Box
          key={c}
          title={s ? `#${id}` : undefined}
          onClick={s ? () => onJump(id, i, total) : undefined}
          sx={{
            width: CELL,
            height: CELL,
            borderRadius: "2px",
            flexShrink: 0,
            background: s ? COLORS[s] : EMPTY,
            cursor: s ? "pointer" : "default",
            ...(local === selectedIdx && selectedIdx >= 0
              ? { outline: "2px solid #1d1d1f", outlineOffset: "1px" }
              : null),
          }}
        />,
      );
    }
    rows.push(
      <Box
        key={r}
        sx={{
          position: "absolute",
          top: r * ROW_H,
          left: 0,
          display: "flex",
          gap: `${GAP}px`,
        }}
      >
        {cells}
      </Box>,
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        px: "14px",
        py: "10px",
        borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 500,
            color: "var(--dk-text-3, #aeaeb2)",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            fontFamily: SF,
          }}
        >
          {t("ReviewProgress", "리뷰 현황")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Typography
            sx={{
              fontSize: "11px",
              color: "var(--dk-text-3, #aeaeb2)",
              fontFamily: SF,
            }}
          >
            {total.toLocaleString()}
          </Typography>
          <Tooltip title={paused ? t("PollPausedMsg", "변화가 없어 자동 조회를 멈췄습니다") : t("Refresh", "새로고침")}>
            <Box
              component="button"
              onClick={() => setResume((v) => v + 1)}
              sx={{
                display: "flex",
                border: 0,
                p: 0,
                background: "none",
                cursor: "pointer",
                color: paused ? "#ff9f0a" : "var(--dk-text-3, #aeaeb2)",
              }}
            >
              <RefreshCw size={12} />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <Box
        ref={scrollRef}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        sx={{ height: VIEW_H, overflowY: "auto", overflowX: "hidden" }}
      >
        {/* 전체 높이만 잡아두고 보이는 줄만 그린다 (수백만 칸을 전부 DOM에 올릴 수 없다) */}
        <Box sx={{ position: "relative", height: rowCount * ROW_H }}>{rows}</Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        {legend.map(([k, label]) => (
          <Box key={k} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "2px",
                background: COLORS[k],
              }}
            />
            <Typography
              sx={{
                fontSize: "10px",
                color: "var(--dk-text-3, #aeaeb2)",
                fontFamily: SF,
              }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
